import { useState } from 'react';
import { Button, GetProp, Image, Switch, Upload, UploadProps } from 'antd';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';

// services
import NotificationService from 'core/services/notification';
import { onUpdateScreen } from 'modules/splashScreen/service/splashScreen';

// models
import { SplashScreen } from 'modules/splashScreen/models/splashScreen';

// components
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';

// styles
import styles from './item.module.scss';
import { isRTL } from 'core/services/language';
import { useTranslation } from 'react-i18next';

interface ScreenItemProps {
	introItem: SplashScreen;
	isReorder: boolean;
	onDelete: any;
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function ScreenItemComponent({ introItem, isReorder, onDelete }: ScreenItemProps) {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);
	const [isStatusLoading, setIsStatusLoading] = useState(false);
	const [isImageUploading, setIsImageUploading] = useState(false);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: introItem.id,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		marginBottom: '20px',
		backgroundColor: 'white',
		cursor: 'default',
	};

	const onStatusChange = async (checked: boolean) => {
		try {
			setIsStatusLoading(true);

			const updateScreenPayload = new FormData();
			updateScreenPayload.append('_method', 'put');
			updateScreenPayload.append('active', checked ? '1' : '0');
			await onUpdateScreen(introItem.id, updateScreenPayload);

			setIsStatusLoading(false);
			NotificationService.success('Success', 'Screen status changed successfully');
		} catch (error) {
			setIsStatusLoading(false);
			NotificationService.error('Ops!', 'Error occurred when updating screen status');
		}
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';

		if (!isJpgOrPng) {
			NotificationService.error('Only JPG/PNG format are acceptable');
			return false;
		}
	};

	const handleUpload = async (options: any) => {
		setIsImageUploading(true);
		const { file } = options;

		const formData = new FormData();
		formData.append('_method', 'put');
		formData.append('image', file);

		try {
			const response = await onUpdateScreen(introItem.id, formData);
			introItem.image_url = response.data.data.image_url;
			setIsImageUploading(false);
			NotificationService.success('Success', 'Splash screen image updated successfully');
		} catch (error) {
			setIsImageUploading(false);
			NotificationService.error('Ops!', 'Error happened when updating splash screen image');
		}
	};

	const uploadProps: UploadProps = {
		name: 'image',
		showUploadList: false,
		beforeUpload: beforeUpload,
		customRequest: handleUpload,
		accept: '.png,.jpeg,.jpg',
	};

	return (
		<>
			<div className="sectionContainer" ref={setNodeRef} style={style} {...attributes}>
				<div className={styles.item_header}>
					<div className={styles.item_header_data}>
						{isReorder && (
							<span
								{...listeners}
								style={{
									cursor: 'move',
									position: 'absolute',
								}}>
								<Icon name="sort" />
							</span>
						)}

						<img alt="caption" src={introItem.image_url} style={{ marginInlineStart: isReorder ? '28px' : 0, transition: 'all .2s' }} />

						<div>
							<h5>{isRTL() ? introItem.title.ar : introItem.title.en}</h5>
						</div>
					</div>

					{!isReorder && (
						<div className={styles.item_header_actions}>
							<AuthActionComponent action="mobile_app_intro_slider_update">
								<div className="d-flex align-items-center gap-1">
									<Switch defaultChecked={introItem.active} loading={isStatusLoading} onChange={onStatusChange} />
								</div>
							</AuthActionComponent>

							<Button style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }} onClick={() => setVisible(true)}>
								{t('COMMON.VIEW')}
							</Button>

							<AuthActionComponent action="mobile_app_intro_slider_update">
								<Upload {...uploadProps}>
									<Button
										style={{ background: '#50CD89', color: '#fff', border: 'none' }}
										loading={isImageUploading}
										icon={<Icon name="upload" color="#fff" />}>
										{t('COMMON.CHANGE')}
									</Button>
								</Upload>
							</AuthActionComponent>
							<AuthActionComponent action="mobile_app_intro_slider_delete">
								<Button type="link" className="p-0" onClick={() => onDelete(introItem.id)}>
									<Icon name="delete" width={40} height={40} />
								</Button>
							</AuthActionComponent>
						</div>
					)}
				</div>

				{!isReorder && (
					<div className={styles.item_body}>
						<div className={styles.descriptions}>
							<p>{isRTL() ? introItem.description.ar : introItem.description.en}</p>
						</div>
					</div>
				)}
			</div>

			<Image
				style={{ display: 'none' }}
				src={introItem.image_url}
				preview={{
					visible,
					scaleStep: 1,
					src: introItem.image_url,
					onVisibleChange: (value) => {
						setVisible(value);
					},
				}}
			/>
		</>
	);
}

export default ScreenItemComponent;
